import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FaGithub, FaExternalLinkAlt, FaTimes, FaArrowRight } from 'react-icons/fa';
import Section from './layout/Section';
import ScrollReveal from './animations/ScrollReveal';
import SpotlightCard from './effects/SpotlightCard';
import { projects } from '../data/projects';

const DetailBlock = ({ label, children }) => (
  <div>
    <h4 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600 dark:text-blue-400">
      {label}
    </h4>
    <div className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{children}</div>
  </div>
);

function ProjectModal({ project, onClose }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusables = dialogRef.current?.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables?.length) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = overflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  const screenshots = project.media?.images ?? [];

  return createPortal(
    <motion.div
      className="fixed inset-0 z-[300] flex items-start justify-center overflow-y-auto overscroll-contain
                 bg-slate-950/85 px-0 pb-8 pt-[max(1rem,env(safe-area-inset-top))] backdrop-blur-md
                 sm:px-6 sm:pb-10 sm:pt-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      onClick={onClose}
    >
      <motion.div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="project-modal-title"
        onClick={(event) => event.stopPropagation()}
        initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.98 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        className="relative my-0 w-full max-w-5xl overflow-hidden border-slate-200 bg-white shadow-2xl
                   sm:rounded-3xl sm:border dark:border-white/10 dark:bg-slate-900"
      >
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close project details"
          className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full
                     border border-slate-200 bg-white/95 text-slate-600 shadow-lg backdrop-blur-md transition-colors
                     hover:bg-slate-100 hover:text-slate-900 dark:border-white/10 dark:bg-slate-800/95
                     dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-white"
        >
          <FaTimes className="h-4 w-4" aria-hidden="true" />
        </button>

        {/* Cover */}
        <div className="relative h-48 overflow-hidden sm:h-60">
          <img
            src={project.cover}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/70 to-transparent dark:from-slate-900 dark:via-slate-900/70" />
          <div className="absolute bottom-5 left-6 right-16">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600 dark:text-blue-400">
              {project.category} &middot; {project.year}
            </p>
            <h3
              id="project-modal-title"
              className="mt-1.5 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl dark:text-white"
            >
              {project.title}
            </h3>
          </div>
        </div>

        <div className="space-y-8 p-6 sm:p-8">
          <p className="text-[15px] leading-relaxed text-slate-700 dark:text-slate-300">
            {project.overview}
          </p>

          {project.outcome?.length > 0 && (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {project.outcome.map((metric) => (
                <div
                  key={metric.label}
                  className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3.5
                             dark:border-white/[0.08] dark:bg-white/[0.03]"
                >
                  <p className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
                    {metric.value}
                  </p>
                  <p className="mt-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-500">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
            <DetailBlock label="The problem">
              <p>{project.problem}</p>
            </DetailBlock>
            <DetailBlock label="The solution">
              <p>{project.solution}</p>
            </DetailBlock>
            <DetailBlock label="My role">
              <p>{project.role}</p>
            </DetailBlock>
            <DetailBlock label="Key features">
              <ul className="space-y-1.5">
                {project.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <span
                      aria-hidden="true"
                      className="mt-[7px] h-1 w-1 flex-none rounded-full bg-blue-500"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </DetailBlock>
          </div>

          <DetailBlock label="Technologies">
            <ul className="flex flex-wrap gap-2">
              {project.tech.map((tech) => (
                <li
                  key={tech}
                  className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium
                             text-slate-600 dark:border-white/[0.08] dark:bg-white/[0.04] dark:text-slate-300"
                >
                  {tech}
                </li>
              ))}
            </ul>
          </DetailBlock>

          {project.lesson && (
            <blockquote
              className="rounded-xl border-l-2 border-blue-500 bg-blue-500/[0.04] px-5 py-4 text-sm
                         italic leading-relaxed text-slate-700 dark:text-slate-300"
            >
              {project.lesson}
            </blockquote>
          )}

          {screenshots.length > 0 && (
            <div>
              <h4 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-blue-600 dark:text-blue-400">
                Screens
              </h4>
              <ul className="mt-3 flex snap-x gap-3 overflow-x-auto pb-3">
                {screenshots.map((image, index) => (
                  <li
                    key={image}
                    className="w-36 flex-none snap-start overflow-hidden rounded-xl border border-slate-200
                               bg-slate-100 dark:border-white/[0.08] dark:bg-slate-800"
                  >
                    <img
                      src={`/images/${project.folder}/${image}`}
                      alt={`${project.title} screen ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                      className="h-72 w-full object-cover object-top"
                      onError={(event) => {
                        event.currentTarget.closest('li').style.display = 'none';
                      }}
                    />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {(project.demo || project.github) && (
            <div className="flex flex-wrap gap-3 border-t border-slate-200 pt-6 dark:border-white/[0.08]">
              {project.demo && (
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-6 py-3 text-sm font-semibold
                             text-white transition-transform hover:-translate-y-0.5
                             dark:bg-white dark:text-slate-900"
                >
                  <FaExternalLinkAlt className="h-3 w-3" aria-hidden="true" />
                  Live demo
                </a>
              )}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-6 py-3
                             text-sm font-semibold text-slate-800 transition-colors hover:border-blue-500/50
                             hover:text-blue-600 dark:border-white/10 dark:text-white dark:hover:text-blue-400"
                >
                  <FaGithub className="h-4 w-4" aria-hidden="true" />
                  View code
                </a>
              )}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>,
    document.body
  );
}


export default function Projects() {
  const [activeId, setActiveId] = useState(null);
  const activeProject = projects.find((project) => project.id === activeId) ?? null;

  const close = useCallback(() => setActiveId(null), []);

  return (
    <Section
      id="work"
      eyebrow="Selected work"
      title="Things I’ve built"
      lead="A few projects worth opening. Each one covers the problem, what I built, and how it turned out."
      className="bg-slate-50 dark:bg-slate-900"
    >
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-dotted opacity-40" />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project, index) => (
          <ScrollReveal key={project.id} direction="up" delay={(index % 3) * 0.06} className="h-full">
            <SpotlightCard as="article" className="h-full">
                <button
                  type="button"
                  onClick={() => setActiveId(project.id)}
                  aria-haspopup="dialog"
                  className="flex h-full flex-col text-left"
                >
                  <span className="relative block h-44 overflow-hidden">
                    <img
                      src={project.cover}
                      alt={`${project.title} preview`}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-700 ease-smooth
                                 group-hover:scale-105"
                    />
                    <span className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/10 to-transparent" />
                    <span className="absolute bottom-3 left-4 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/80">
                      {project.category}
                    </span>
                  </span>

                  <span className="flex flex-1 flex-col p-5">
                    <span className="flex items-baseline justify-between gap-3">
                      <span className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                        {project.title}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">{project.year}</span>
                    </span>

                    <span className="mt-2 block text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                      {project.summary}
                    </span>

                    <span className="mt-4 flex flex-wrap gap-1.5">
                      {project.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px]
                                     font-medium text-slate-500 dark:border-white/[0.08] dark:text-slate-400"
                        >
                          {tag}
                        </span>
                      ))}
                    </span>

                    <span
                      className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold
                                 text-blue-600 dark:text-blue-400"
                    >
                      Read the story
                      <FaArrowRight
                        className="h-2.5 w-2.5 transition-transform duration-300 group-hover:translate-x-1"
                        aria-hidden="true"
                      />
                    </span>
                  </span>
                </button>
            </SpotlightCard>
          </ScrollReveal>
        ))}
      </div>

      <AnimatePresence>
        {activeProject && <ProjectModal project={activeProject} onClose={close} />}
      </AnimatePresence>
    </Section>
  );
}
